import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMs365Callback, type CallbackSuccessResult } from "@/hooks/use-ms365-callback";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export function Ms365CallbackPage() {
  const { t } = useTranslation();
  const { state, result, error } = useMs365Callback();

  useEffect(() => {
    if (state === "success" && result && "mode" in result && result.redirect_to) {
      const redirectTo = result.redirect_to;
      const timer = setTimeout(() => {
        window.location.href = redirectTo;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [state, result]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      <div className="w-full max-w-md space-y-6 text-center">
        {state === "loading" && (
          <div className="flex flex-col items-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 dark:text-blue-400" />
            <div>
              <h1 className="text-xl font-semibold text-foreground">{t("connections:ms365Callback.completing")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("connections:ms365Callback.completingDescription")}
              </p>
            </div>
          </div>
        )}

        {state === "success" && result && "mode" in result && (
          <div className="flex flex-col items-center space-y-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/60">
              <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {result.mode === "admin_consent"
                  ? t("connections:ms365Callback.adminConsentGranted")
                  : result.connection_level === "organization"
                    ? t("connections:ms365.sharedConnected")
                    : t("connections:ms365Callback.connected")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {result.mode === "admin_consent" ? (
                  <>{t("connections:ms365Callback.redirecting")}</>
                ) : (
                  <>
                    {(result as CallbackSuccessResult).user?.display_name ||
                    (result as CallbackSuccessResult).user?.email
                      ? t("connections:ms365Callback.userConnected", {
                        name: (result as CallbackSuccessResult).user?.display_name
                          || (result as CallbackSuccessResult).user?.email,
                      })
                      : t("connections:ms365Callback.connectedDescription")}
                  </>
                )}
              </p>
            </div>
            {result.redirect_to ? (
              <Button
                variant="outline"
                onClick={() => { window.location.href = result.redirect_to!; }}
              >
                {t("connections:actions.continue")}
              </Button>
            ) : (
              result.mode === "user_auth" && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { window.location.href = "/"; }}>
                    {t("connections:page.backToChat")}
                  </Button>
                  <Button onClick={() => { window.location.href = "/connections"; }}>
                    {t("connections:actions.goToConnections")}
                  </Button>
                </div>
              )
            )}
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center space-y-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/60">
              <AlertCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">{t("connections:googleCallback.failed")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {error || t("connections:ms365Callback.failedDescription")}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { window.location.href = "/"; }}>
                {t("connections:page.backToChat")}
              </Button>
              <Button onClick={() => { window.location.reload(); }}>
                {t("common:actions.retry")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
